import {
  type GeocodedAddress,
  type InputActivity,
  isWebDataSuccess,
  type NewRoute,
  type ParsedSpreadsheetTab,
  type WebData,
  webDataError,
  webDataFetching,
  webDataNotAsked,
  webDataSuccess,
} from '@cobliteam/api-lib';
import type { UUID } from '@cobliteam/common-types';
import { type Draft, produce } from 'immer';
import type { Reducer } from 'redux';
import type { RoutingAction } from './actions';
import { ActionTypes } from './actionTypes';

export interface RoutingState {
  routingWebData: WebData<NewRoute[]>;
  editRouteWebData: WebData<NewRoute>;
  deleteRoutesWebData: WebData<UUID[]>;
  spreadsheetImportResultAddressesWebData: WebData<InputActivity[]>;
  parsedSpreadsheet: WebData<ParsedSpreadsheetTab[]>;
  loadAddressV2WebData: WebData<GeocodedAddress>;
}

export const routingInitialState: RoutingState = {
  routingWebData: webDataNotAsked(),
  editRouteWebData: webDataNotAsked(),
  deleteRoutesWebData: webDataNotAsked(),
  spreadsheetImportResultAddressesWebData: webDataNotAsked(),
  parsedSpreadsheet: webDataNotAsked(),
  loadAddressV2WebData: webDataNotAsked(),
};

const routingReducer: Reducer<RoutingState, RoutingAction> = produce((draft: Draft<RoutingState>, action: RoutingAction) => {
  switch (action.type) {
    case ActionTypes.ROUTING_REQUEST:
      draft.routingWebData = webDataFetching();
      break;
    case ActionTypes.ROUTING_FAILED:
      draft.routingWebData = webDataError();
      break;
    case ActionTypes.ROUTING_SUCCEEDED:
      draft.routingWebData = webDataSuccess(action.payload);
      break;
    case ActionTypes.EDIT_ROUTE_REQUEST:
      draft.editRouteWebData = webDataFetching();
      break;
    case ActionTypes.EDIT_ROUTE_FAILED:
      draft.editRouteWebData = webDataError();
      break;
    case ActionTypes.EDIT_ROUTE_SUCCEEDED:
      if (isWebDataSuccess(draft.routingWebData)) {
        const updatedRoutes = draft.routingWebData.data.map((route: NewRoute) => (route.id === action.payload.id ? action.payload : route));
        draft.routingWebData.data = updatedRoutes;
      }
      draft.editRouteWebData = webDataSuccess(action.payload);
      break;
    case ActionTypes.DELETE_ROUTES_REQUEST:
      draft.deleteRoutesWebData = webDataFetching();
      break;
    case ActionTypes.DELETE_ROUTES_FAILED:
      draft.deleteRoutesWebData = webDataError();
      break;
    case ActionTypes.DELETE_ROUTES_SUCCEEDED:
      draft.deleteRoutesWebData = webDataSuccess(action.payload);
      if (isWebDataSuccess(draft.routingWebData)) {
        const updatedRoutes = draft.routingWebData.data.filter((route: NewRoute) => !action.payload.includes(route.id));
        draft.routingWebData.data = updatedRoutes;
      }
      break;
    case ActionTypes.PARSE_SPREADSHEET_REQUEST:
      draft.parsedSpreadsheet = webDataFetching();
      break;
    case ActionTypes.PARSE_SPREADSHEET_FAILED:
      draft.parsedSpreadsheet = webDataError(action.type);
      break;
    case ActionTypes.PARSE_SPREADSHEET_SUCCEEDED:
      draft.parsedSpreadsheet = webDataSuccess(action.payload);
      break;
    case ActionTypes.IMPORT_ACTIVITIES_SPREADSHEET_REQUEST:
      draft.spreadsheetImportResultAddressesWebData = webDataFetching();
      break;
    case ActionTypes.IMPORT_ACTIVITIES_SPREADSHEET_FAILED:
      draft.spreadsheetImportResultAddressesWebData = webDataError();
      break;
    case ActionTypes.IMPORT_ACTIVITIES_SPREADSHEET_SUCCEEDED:
      draft.spreadsheetImportResultAddressesWebData = webDataSuccess(action.payload);
      break;
  }
}, routingInitialState);

export default routingReducer;
